import React, { useState } from 'react';
import { Alert, App, Button, Card, Checkbox, Descriptions, Input, Modal, Result, Space, Spin, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import hrmsService from '@/services/hrms.service';
import { formatDate, formatMoney } from '@/utils/format';

const { Title, Text } = Typography;

function money(v: unknown, currency?: string) {
  return formatMoney(v, { currency: currency || 'AED', nullDisplay: '—' });
}

const OfferAcceptPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [agree, setAgree] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const { data: offer, isLoading, error } = useQuery({
    queryKey: ['public-offer', token],
    queryFn: () => hrmsService.getPublicOffer(token!),
    enabled: Boolean(token),
  });

  const respond = async (accept: boolean, notes?: string) => {
    if (!token) return;
    setSubmitting(true);
    try {
      await hrmsService.respondPublicOffer(token, { accept, notes });
      message.success(accept ? 'Thank you — your acceptance has been recorded' : 'Your response has been recorded');
      setDeclineOpen(false);
      await queryClient.invalidateQueries({ queryKey: ['public-offer', token] });
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Response failed');
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async () => {
    if (!token) return;
    setDownloading(true);
    try {
      const blob = await hrmsService.getPublicOfferPdf(token);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Offer-${offer?.offer_number || 'letter'}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch {
      message.error('Could not download the offer letter');
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div style={{ maxWidth: 640, margin: '48px auto', padding: 24 }}>
        <Result status="404" title="Offer not available" subTitle="This link is invalid or the offer was withdrawn. Please contact the recruitment team." />
      </div>
    );
  }

  const status = String(offer.status || '');
  const allowances: Array<{ name: string; amount: number }> = Array.isArray(offer.allowances) ? offer.allowances : [];
  const gross = Number(offer.basic_salary || 0) + allowances.reduce((s, a) => s + Number(a.amount || 0), 0);
  const contingencies = [
    offer.contingencies?.medical && 'medical fitness',
    offer.contingencies?.visa && 'visa / work permit approval',
    offer.contingencies?.background && 'background verification',
  ].filter(Boolean);

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '32px 24px' }}>
      <Title level={2} style={{ marginBottom: 0 }}>
        Your job offer
      </Title>
      <Text type="secondary">
        {offer.candidate_name ? `${offer.candidate_name} · ` : ''}
        {offer.offer_number}
      </Text>

      {status === 'Sent' && offer.expires_at && (
        <Alert
          style={{ marginTop: 16 }}
          type="info"
          showIcon
          message={`Please respond by ${formatDate(offer.expires_at)}.`}
        />
      )}

      <Card
        style={{ marginTop: 16 }}
        extra={
          ['Sent', 'Accepted'].includes(status) && (
            <Button icon={<DownloadOutlined />} loading={downloading} onClick={downloadPdf}>
              Offer letter (PDF)
            </Button>
          )
        }
        title={offer.job_title}
      >
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Employment">{[offer.employment_type, offer.contract_type].filter(Boolean).join(' · ') || '—'}</Descriptions.Item>
          <Descriptions.Item label="Join date">{offer.join_date ? String(offer.join_date).slice(0, 10) : '—'}</Descriptions.Item>
          <Descriptions.Item label="Basic salary (monthly)">{money(offer.basic_salary, offer.currency)}</Descriptions.Item>
          {allowances.map((a) => (
            <Descriptions.Item key={a.name} label={a.name}>
              {money(a.amount, offer.currency)}
            </Descriptions.Item>
          ))}
          {allowances.length > 0 && (
            <Descriptions.Item label={<strong>Gross monthly</strong>}>
              <strong>{money(gross, offer.currency)}</strong>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Probation">{offer.probation_months != null ? `${offer.probation_months} months` : '—'}</Descriptions.Item>
        </Descriptions>
        {contingencies.length > 0 && (
          <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
            This offer is subject to {contingencies.join(', ')}.
          </Text>
        )}

        {status === 'Sent' ? (
          <div style={{ marginTop: 20 }}>
            <Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)}>
              I have read the offer letter and accept the terms above.
            </Checkbox>
            <Space style={{ marginTop: 12, display: 'flex' }}>
              <Button type="primary" disabled={!agree} loading={submitting} onClick={() => respond(true)}>
                Accept offer
              </Button>
              <Button danger disabled={submitting} onClick={() => setDeclineOpen(true)}>
                Decline
              </Button>
            </Space>
          </div>
        ) : (
          <Result
            style={{ padding: '24px 0 0' }}
            status={status === 'Accepted' ? 'success' : status === 'Expired' ? 'warning' : 'info'}
            title={
              status === 'Accepted'
                ? 'You accepted this offer'
                : status === 'Declined'
                  ? 'You declined this offer'
                  : status === 'Expired'
                    ? 'This offer has expired'
                    : `Offer ${status}`
            }
            subTitle={status === 'Accepted' ? 'Our HR team will contact you with the next steps for joining.' : 'Please contact the recruitment team if you have questions.'}
          />
        )}
      </Card>

      <Modal
        open={declineOpen}
        title="Decline offer"
        okText="Decline offer"
        okButtonProps={{ danger: true, disabled: !declineReason.trim(), loading: submitting }}
        onOk={() => respond(false, declineReason.trim())}
        onCancel={() => setDeclineOpen(false)}
      >
        <Text>Please tell us briefly why you are declining. This helps us improve.</Text>
        <Input.TextArea rows={4} style={{ marginTop: 8 }} value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} />
      </Modal>
    </div>
  );
};

export default OfferAcceptPage;
